import { Info } from 'react-feather';
import { createPortal } from 'react-dom';
import React, { useEffect, useReducer, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { DeleteIcon, DraftIcon, FeaturedIcon, PDFIcon, Trash } from '../../../../assets/images';
import {
  AddFilesLoading,
  AddFilesText,
  chunkInput,
  uploadConfigs,
  UploadText,
} from '../data/constants';
import {
  deleteCredential,
  GetAuthenticateWithExitingCred,
  getGoogleAuthentication,
  uploadFileToFolder,
} from '../services/kb';
import { CredentialListProp, UploadKBFolderProps, UploadKBFolderState } from '../kb.props';
import {
  initialStatesUploadKnowledgeBaseFolderReducer,
  UploadKnowledgeBaseFolderReducer,
  UploadKnowledgeBaseAction,
} from '../kb.reducer';
import { truncateData } from '../../../../services';
import { MakeToast, FileUploader, ActionModal, LoaderOverlay } from '../../../../components';
import StyledTooltip from '../../../../components/tooltip';
import FolderHierarchy from './folder-hierarchy';
import AwsAuthModal from './aws-auth-modal';

function UploadKBFolder({
  open,
  idToUpload,
  dispatcher,
  service,
  credentials,
  redirect,
  handleCancelUploadClick,
  onUploadStarted,
  onUploadSuccess,
  onDriveSyncStarted,
  onDriveSyncFailed,
  fileUploaderTriggerId = 'uploadFileKb',
}: UploadKBFolderProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [componentState, dispatch] = useReducer<
    React.Reducer<UploadKBFolderState, UploadKnowledgeBaseAction>
  >(UploadKnowledgeBaseFolderReducer, initialStatesUploadKnowledgeBaseFolderReducer);
  const { state } = useLocation();
  const { folderId } = state || {};
  const [addFilesButton, setAddFilesButton] = useState<string>(AddFilesText);
  const [localCredentials, setLocalCredentials] = useState<CredentialListProp[]>(credentials || []);

  useEffect(() => {
    // Keep local state in sync when parent updates it (optional)
    setLocalCredentials(credentials || []);
  }, [credentials]);

  const onChangeHandler = (key: string, value: string | number) => {
    dispatch({ type: 'SHOW_ERROR', payload: '' });
    dispatch({ type: 'SHOW_OVERLAP_ERROR', payload: '' });
    dispatch({ type: 'SET_VALUE', payload: { key, value } });
  };

  const handleCancelClick = () => {
    dispatch({ type: 'RESET_UPLOAD' });
    if (handleCancelUploadClick) {
      handleCancelUploadClick();
    }
  };

  const handleUploadFile = async () => {
    if (componentState?.files?.length === 0) {
      MakeToast('error', 'Please upload at least one file.');
      return;
    }
    dispatch({ type: 'SET_LOADING', payload: true });
    const formData = new FormData();
    componentState?.files?.forEach((file: File) => {
      formData.append('file', file);
    });
    setAddFilesButton(AddFilesLoading);
    formData.append('chunk_size', componentState.chunk_words.toString());
    if (componentState.chunk_words === 0) {
      MakeToast('error', `Chunk size Can't be empty or zero!`);
      dispatch({
        type: 'SHOW_ERROR',
        payload: `Chunk size Can't be empty or zero!`,
      });
      setAddFilesButton(AddFilesText);
      dispatch({ type: 'SET_LOADING', payload: false });
      return;
    }
    if (componentState.chunk_words <= componentState.chunk_overlap) {
      MakeToast('error', "Chunk Overlap Can't be More than Chunk size!");
      dispatch({
        type: 'SHOW_OVERLAP_ERROR',
        payload: `Can't be More than Chunk size`,
      });
      setAddFilesButton(AddFilesText);
      dispatch({ type: 'SET_LOADING', payload: false });
      return;
    }
    if (componentState.chunk_overlap > Math.floor(componentState.chunk_words / 2)) {
      MakeToast(
        'error',
        `Chunk overlap must be less than ${Number(Math.floor(componentState.chunk_words / 2))}`,
      );
      dispatch({
        type: 'SHOW_OVERLAP_ERROR',
        payload: `Chunk overlap must be less than ${Math.floor(componentState.chunk_words / 2)}`,
      });
      setAddFilesButton(AddFilesText);
      dispatch({ type: 'SET_LOADING', payload: false });
      return;
    }
    formData.append('chunk_overlap', componentState.chunk_overlap.toString());
    try {
      const res = await uploadFileToFolder(formData, idToUpload! || folderId);
      if (res.status === 200) {
        const currentFolderId = idToUpload || folderId;
        MakeToast('success', res.data.message);
        handleCancelClick();
        dispatcher?.({
          type: 'DATA_UPDATED',
          payload: (prev: boolean) => !prev,
        });
        if (currentFolderId) {
          onUploadStarted?.(currentFolderId);
          onUploadSuccess?.(currentFolderId);
        }
      }
    } catch (err: unknown) {
      type AxiosErrorLike = { response?: { status?: number; data?: { message?: string } } };
      if (
        typeof err === 'object' &&
        err !== null &&
        'response' in err &&
        (err as AxiosErrorLike).response?.status
      ) {
        MakeToast('error', (err as AxiosErrorLike).response?.data?.message || 'Upload failed');
      } else {
        MakeToast('error', 'Something went wrong!');
      }
    } finally {
      setAddFilesButton(AddFilesText);
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const handleFileRejected = () => {
    MakeToast('error', 'Only .pdf, .txt and .docx files are allowed.');
  };

  const handleOAuthAuthClick = async () => {
    const userId = localStorage.getItem('user_id');
    let loggedInUser: number = 0;
    if (userId) {
      loggedInUser = JSON.parse(userId);
    }

    try {
      const searchParams = new URLSearchParams(location.search);
      navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true });
      if (service === 'G-drive') {
        await getGoogleAuthentication(idToUpload, loggedInUser);
        navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true });
      } else if (service === 'aws') {
        dispatch({ type: 'AWS_AUTH_MODAL', payload: true });
      }
    } catch (error: unknown) {
      let message = 'An error occurred';
      if (typeof error === 'string') {
        message = error;
      } else if (error instanceof Error && error.message) {
        message = error.message;
      }
      MakeToast('error', message);
    }
  };

  // Handler for deleting credentials (dummy implementation)
  const handleDeleteCredentials = async (
    event: React.MouseEvent | React.KeyboardEvent,
    credentialId: number,
  ) => {
    event.stopPropagation();

    try {
      const response = await deleteCredential(credentialId);
      if (response?.status === 200) {
        const updatedCredentials = localCredentials.filter((c) => c.id !== credentialId);
        setLocalCredentials(updatedCredentials);
        MakeToast('success', 'Credential deleted successfully.');
      } else {
        MakeToast(
          'error',
          `Failed to delete credential. Server responded with status ${response?.status}.`,
        );
      }
    } catch (error) {
      MakeToast('error', `Something went wrong while deleting. ${error || ''}`);
    }
  };

  const handleContinueWithCredentials = async (credentialId: number, storeId: number) => {
    try {
      const response = await GetAuthenticateWithExitingCred(storeId, credentialId);

      if (response?.status === 200) {
        const searchParams = new URLSearchParams(location.search);
        searchParams.set('status', 'authenticated');
        navigate(`${location.pathname}?${searchParams.toString()}`, {
          replace: true,
        });
        dispatch({ type: 'FOLDER_STRUCTURE_MODAL_OPEN', payload: true });
        dispatch({ type: 'SET_SERVICE', payload: service });
        dispatch({ type: 'FOLDER_ID', payload: idToUpload });
        MakeToast('success', 'authenticated successfully.');
      } else {
        MakeToast(
          'error',
          `Failed to delete credential. Server responded with status ${response?.status}.`,
        );
      }
    } catch (error) {
      MakeToast('error', `Something went wrong while deleting. ${error || ''}`);
    }
  };

  useEffect(() => {
    if (redirect) {
      const searchParams = new URLSearchParams(location.search);
      searchParams.set('status', 'authenticated');
      navigate(`${location.pathname}?${searchParams.toString()}`, {
        replace: true,
      });
      dispatch({ type: 'FOLDER_STRUCTURE_MODAL_OPEN', payload: true });
      dispatch({ type: 'SET_SERVICE', payload: service });
      dispatch({ type: 'FOLDER_ID', payload: idToUpload });
    }
  }, [redirect]);

  const currentUploadConfig = uploadConfigs?.find((config) => config.id === service);

  if (!currentUploadConfig) {
    return null;
  }

  const isOAuthBased = currentUploadConfig.service !== 'system';

  return (
    <>
      <ActionModal
        className="w-[550px] upload-kb-modal"
        open={open}
        modalLogo={FeaturedIcon}
        modalTitle={<h3 className="text-base font-bold">{currentUploadConfig.title}</h3>}
        resetData={handleCancelClick}
        confirmButtonText={addFilesButton}
        saveData={handleUploadFile}
        disableSaveButton={addFilesButton === AddFilesLoading}
      >
        {isOAuthBased ? (
          <div className="flex flex-col items-center justify-center gap-3">
            <span className="font-bold text-lg self-start">Continue With</span>
            {localCredentials &&
              localCredentials.length > 0 &&
              localCredentials.map((credential: CredentialListProp) => (
                <button
                  key={credential.user_id}
                  type="button"
                  onClick={() => {
                    handleContinueWithCredentials(credential.id, idToUpload);
                  }}
                  className="bg-slate-100 text-black w-full py-2 rounded-md flex justify-between items-center gap-2 p-2"
                >
                  <div className="flex justify-start items-center gap-2">
                    <div className="rounded-full w-[40px] h-[40px] flex justify-center items-center bg-[#c4c7d5] font-bold text-blue-900">
                      {credential.email.charAt(0).toUpperCase()}
                    </div>
                    <span>{truncateData(credential.email, 40)}</span>
                  </div>
                  <button
                    type="button"
                    className="p-0 border-none bg-transparent z-10"
                    onClick={(e) => handleDeleteCredentials(e, credential.id)}
                    tabIndex={0}
                    onKeyDown={(e) => handleDeleteCredentials(e, credential.id)}
                    aria-label="Delete credentials"
                  >
                    <img src={Trash} alt="icon" className="h-5 w-5" />
                  </button>
                </button>
              ))}
            {localCredentials && localCredentials.length > 0 && (
              <span className="font-bold text-lg">OR</span>
            )}
            <button
              type="button"
              className="bg-blue-900 text-white w-full py-2 rounded-md flex justify-center items-center gap-2"
              onClick={handleOAuthAuthClick}
            >
              <img src={currentUploadConfig.buttonIcon} alt="icon" className="h-5 w-5" />
              {currentUploadConfig.oauthButtonText}
            </button>
            <div className="border border-dashed w-full p-6 text-center bg-[#F8F9FD]">
              <img src={currentUploadConfig.icon} alt="provider" className="mx-auto mb-2" />
              <p className="font-semibold">{currentUploadConfig.providerLabel}</p>
              <p className="text-xs text-gray-600">{currentUploadConfig.acceptedFormatsNote}</p>
            </div>
          </div>
        ) : (
          // SYSTEM BASED UI
          <div className="flex flex-col gap-y-2">
            <FileUploader
              accept={currentUploadConfig.acceptedFileTypes}
              supportedFormats={currentUploadConfig.acceptedFormatsNote}
              multiple
              maxFiles={Infinity}
              onFilesAccepted={(files) => {
                dispatch({ type: 'ADD_FILES', payload: files as File[] });
              }}
              onFileRejected={handleFileRejected}
              triggerId={fileUploaderTriggerId}
              backgroundColor="#F8F9FD"
              height="280px"
              enableUploadCloudIcon
            />

            {/* Uploaded Files List */}
            {componentState?.files?.length > 0 && (
              <div className="mt-2">
                <p className="text-sm text-[#676767] font-bold mb-[6px]">{UploadText}</p>
                <ul className="list-disc list-inside text-lg">
                  {componentState?.files?.map((file: File, index: number) => {
                    const fileExtension = file.name.split('.').pop()?.toLowerCase();
                    const fileIcon = fileExtension === 'pdf' ? PDFIcon : DraftIcon;

                    return (
                      <div
                        className="border !border-[#11AF22] rounded-md mb-2 p-2 flex items-center justify-between"
                        key={file.name}
                      >
                        <div className="flex items-center">
                          <img src={fileIcon} className="mr-2" alt="file" />
                          <div>
                            <span className="block text-sm font-medium">
                              {truncateData(file.name, 20)}
                            </span>
                            <span className="block text-sm font-normal text-gray-600">
                              {(file.size / 1024).toFixed(2)} KB
                            </span>
                          </div>
                        </div>
                        <button
                          type="button"
                          className="mr-1.5"
                          onClick={() => dispatch({ type: 'REMOVE_FILE', payload: index })}
                        >
                          <img src={DeleteIcon} height={20} alt="delete" />
                        </button>
                      </div>
                    );
                  })}
                </ul>
              </div>
            )}

            {componentState.files.length > 0 &&
              chunkInput?.map((i) => {
                const isChunkWords = i.id === 'chunk_words';
                const fieldError = isChunkWords
                  ? componentState.showError
                  : componentState.showOverLapError;

                return (
                  <div className="mt-2" key={i.id}>
                    <div className="flex items-center gap-1 mb-[6px]">
                      <span className="text-sm text-[#676767] font-bold">
                        {i.label}
                        {i.required && <span className="text-red-600">* </span>}
                      </span>
                      <StyledTooltip title={i.infoIconText} placement="right">
                        <span title="Information" aria-label="Information">
                          <Info size={14} className="text-[#676767]" />
                        </span>
                      </StyledTooltip>
                    </div>
                    {fieldError && <span className="text-red-600 text-xs">{fieldError}</span>}
                    <div className="radio-btns rounded-md">
                      <div className="flex items-center">
                        <span className="text-sm w-1/3">{i.labelHelper}</span>
                        <input
                          key={i.id}
                          type={i.type}
                          className={`ml-2 placeholder:text-sm ${
                            fieldError ? 'focus:border-red-600 border-red-600' : 'border-[#E4E4E7]'
                          } rounded-md w-full`}
                          placeholder={i.placeholder}
                          disabled={componentState.files.length === 0 || componentState.loading}
                          min={i.min}
                          {...(i.id === 'chunk_overlap'
                            ? { max: Math.floor(componentState.chunk_words / 2) }
                            : {})}
                          value={
                            i.id === 'chunk_words'
                              ? componentState.chunk_words || ''
                              : componentState.chunk_overlap || ''
                          }
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, '');
                            onChangeHandler(i.id, Number(value));
                          }}
                          onKeyDown={(e) => {
                            if (['e', 'E', '+', '-'].includes(e.key)) e.preventDefault();
                          }}
                        />
                      </div>
                      {!isChunkWords &&
                        componentState.chunk_overlap >
                          Math.floor(componentState.chunk_words / 2) && (
                          <span className="text-red-600">{i.error}</span>
                        )}
                    </div>
                  </div>
                );
              })}
          </div>
        )}
      </ActionModal>
      <AwsAuthModal
        idToUpload={idToUpload}
        dispatcher={dispatch}
        service={service}
        isLoading={componentState.loading}
        open={componentState.openAuthAwsModal}
      />
      <FolderHierarchy
        dispatch={dispatch ?? (() => {})}
        service={componentState.service}
        open={componentState.folderStructureModalOpen}
        folderId={componentState.folderId}
        handleUploadCancelClick={handleCancelClick}
        onSyncStarted={(storeFolderId) => {
          onDriveSyncStarted?.(storeFolderId);
        }}
        onSyncFailed={(storeFolderId) => {
          onDriveSyncFailed?.(storeFolderId);
        }}
        onSyncSuccess={() => {
          dispatcher?.({
            type: 'DATA_UPDATED',
            payload: (prev: boolean) => !prev,
          });
        }}
      />
      {componentState.loading && createPortal(<LoaderOverlay />, document.body)}
    </>
  );
}

export default UploadKBFolder;
